import {
  AlertCategory,
  AlertEpisodeDisplayedState,
  AlertPatientListSortProperty,
  ALERT_CATEGORY_ENUM,
  ALERT_DISPLAYED_STATE_ENUM,
  ALERT_ORIGIN_ENUM,
  ALERT_PATIENT_LIST_SORT_PROPERTIES,
  DEFAULT_PAGE_SIZE,
  Formatters,
  SelectedFilter,
  ShortcutEnum,
  TablePaging,
  TableSorting,
} from '@wallaby-ui/document';

/** The saved-filter component of the alerts list's own saved filters (MERIE `Components.ALERT_LIST`). */
export const ALERT_LIST_COMPONENT = 'alertList';

/**
 * The alerts list's filter form, and the stored shape of its saved filters: MERIE's `AlertListFilterDto` field for
 * field. The floating shortcut is the FE's alone: MERIE drops it, so the dates are resolved here before every request.
 */
export type AlertListPageFilters = {
  patientFullName?: string;
  patientIdentificationNumber?: string;
  patientSrcId?: string;
  totalAlertWeightFrom?: number;
  totalAlertWeightTo?: number;
  categories?: AlertCategory[];
  userFilterIds?: number[];
  weightFrom?: number;
  weightTo?: number;
  components?: string[];
  firstActiveFrom?: string;
  firstActiveTo?: string;
  firstActiveFloatingShortcutName?: ShortcutEnum | null;
  states?: AlertEpisodeDisplayedState[];
};

/** State defaults to Active (PDW-183); clearing it shows every state. */
export const DEFAULT_ALERT_LIST_FILTERS: AlertListPageFilters = {states: ['ACTIVE']};
export const DEFAULT_ALERT_LIST_SORTING: TableSorting = {};
export const DEFAULT_ALERT_LIST_PAGING: TablePaging = {};

/** The filter as the saved-filter box compares it: a floating range is the shortcut, whatever dates it resolved to. */
export function normalizeAlertListFiltersForCompare(filter: AlertListPageFilters): AlertListPageFilters {
  const copy: AlertListPageFilters = {...(filter ?? {})};
  if (copy.firstActiveFloatingShortcutName) {
    delete copy.firstActiveFrom;
    delete copy.firstActiveTo;
  }
  return copy;
}

/**
 * The query string of `GET /api/alerts`: the filter with each set as one comma-separated value, the page and the
 * user's sort. Core appends the default keys the sort leaves out, so only the user's choice is sent.
 */
export function buildAlertListParams(filters: AlertListPageFilters, paging: TablePaging, sorting: TableSorting): URLSearchParams {
  const params = buildAlertListExportParams(filters, sorting);
  params.set('page', String(paging.page ?? 0));
  params.set('size', String(paging.pageSize ?? DEFAULT_PAGE_SIZE));
  return params;
}

/**
 * The query string of `GET /api/alerts/xlsx` (PDW-253): the list's filter and the user's sort with no page, since the
 * file holds the patients in the page's order up to the export limit.
 */
export function buildAlertListExportParams(filters: AlertListPageFilters, sorting: TableSorting): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','));
      return;
    }
    params.set(key, String(value));
  };
  set('patientFullName', filters.patientFullName);
  set('patientIdentificationNumber', filters.patientIdentificationNumber);
  set('patientSrcId', filters.patientSrcId);
  set('totalAlertWeightFrom', filters.totalAlertWeightFrom);
  set('totalAlertWeightTo', filters.totalAlertWeightTo);
  set('categories', filters.categories);
  set('userFilterIds', filters.userFilterIds);
  set('weightFrom', filters.weightFrom);
  set('weightTo', filters.weightTo);
  set('components', filters.components);
  set('firstActiveFrom', filters.firstActiveFrom);
  set('firstActiveTo', filters.firstActiveTo);
  set('states', filters.states);
  const field = sorting.field as AlertPatientListSortProperty | undefined;
  if (field && sorting.direction && ALERT_PATIENT_LIST_SORT_PROPERTIES.includes(field))
    params.append('sort', `${field},${sorting.direction}`);
  return params;
}

type AlertListChipLookups = {
  getAlertLabel: (id: number) => string;
};

/** The "Active filters" chips of the alerts list, one per filter, in the order of the filter card. */
export function buildAlertListSelectedFilters(
  filters: AlertListPageFilters,
  formatters: Pick<Formatters, 't' | 'tEnum' | 'formatDate'>,
  lookups: AlertListChipLookups
): SelectedFilter[] {
  const {t, tEnum, formatDate} = formatters;
  return [
    {name: 'patientFullName', label: t('filter.patient.fullName'), value: filters.patientFullName},
    {name: 'patientIdentificationNumber', label: t('filter.patient.identificationNumber'), value: filters.patientIdentificationNumber},
    {name: 'patientSrcId', label: t('filter.patient.sourceId'), value: filters.patientSrcId},
    {
      name: ['totalAlertWeightFrom', 'totalAlertWeightTo'],
      label: t('alert.list.column.totalAlertWeight'),
      value: `${filters.totalAlertWeightFrom ?? ''} - ${filters.totalAlertWeightTo ?? ''}`,
    },
    {
      name: 'categories',
      label: t('alert.board.filter.category'),
      value: (filters.categories ?? []).map((v) => tEnum(ALERT_CATEGORY_ENUM, v)).join(', '),
    },
    {
      name: 'userFilterIds',
      label: t('alert.board.filter.alert'),
      value: (filters.userFilterIds ?? []).map(lookups.getAlertLabel).join(', '),
    },
    {name: ['weightFrom', 'weightTo'], label: t('alert.weight'), value: `${filters.weightFrom ?? ''} - ${filters.weightTo ?? ''}`},
    {
      name: 'components',
      label: t('hai.properties.origin'),
      value: (filters.components ?? []).map((v) => tEnum(ALERT_ORIGIN_ENUM, v)).join(', '),
    },
    {
      name: ['firstActiveFrom', 'firstActiveTo', 'firstActiveFloatingShortcutName'],
      label: t('alert.list.column.firstActive'),
      value: `${formatDate(filters.firstActiveFrom ?? '')} - ${formatDate(filters.firstActiveTo ?? '')}`,
    },
    {
      name: 'states',
      label: t('alert.list.column.state'),
      value: (filters.states ?? []).map((v) => tEnum(ALERT_DISPLAYED_STATE_ENUM, v)).join(', '),
      defaultValue: DEFAULT_ALERT_LIST_FILTERS.states,
    },
  ];
}
